import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Phone, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';

const SOSButton = ({ rideId, deliveryId }) => {
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', relationship: '' });
  const [sending, setSending] = useState(false);
  const [confirmHold, setConfirmHold] = useState(false);
  const holdTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const api = (await import('../utils/api')).default;
        const { data } = await api.get('/users/me/emergency-contact');
        setContact(data && data.phone ? data : null);
        if (data) setForm({ name: data.name || '', phone: data.phone || '', relationship: data.relationship || '' });
      } catch { /* ignore */ }
    })();
  }, []);

  const captureLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      () => resolve(null),
      { timeout: 4000, maximumAge: 30000 }
    );
  });

  const fireSOS = async () => {
    if (!contact || !contact.phone) {
      setShowContactForm(true);
      return;
    }
    setSending(true);
    try {
      const api = (await import('../utils/api')).default;
      const location = await captureLocation();
      const { data } = await api.post('/sos/trigger', { rideId, deliveryId, location });
      toast({
        title: 'SOS sent',
        description: data.contactNotified
          ? `Alerted ${contact.name} and admin team. Help is on the way.`
          : 'Alert recorded. Admin team notified.',
      });
      setOpen(false);
    } catch (err) {
      toast({ title: 'SOS failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSending(false); }
  };

  const startHold = () => {
    setConfirmHold(true);
    holdTimer.current = setTimeout(fireSOS, 1500);  // 1.5s long-press to confirm
  };
  const cancelHold = () => {
    setConfirmHold(false);
    if (holdTimer.current) clearTimeout(holdTimer.current);
  };

  const saveContact = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ title: 'Name and phone required', variant: 'destructive' });
      return;
    }
    try {
      const api = (await import('../utils/api')).default;
      const { data } = await api.put('/users/me/emergency-contact', form);
      setContact(data);
      setShowContactForm(false);
      toast({ title: 'Emergency contact saved' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="w-full border-red-300 text-red-600 hover:bg-red-50"
        onClick={() => setOpen(true)}
        data-testid="sos-button"
      >
        <AlertTriangle className="h-4 w-4 mr-2" />
        Emergency SOS
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) cancelHold(); }}>
        <DialogContent className="sm:max-w-sm" data-testid="sos-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Emergency SOS
            </DialogTitle>
          </DialogHeader>

          {contact ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Press and hold the button below. We'll alert <strong>{contact.name}</strong> ({contact.phone}) and Tekstar admin team with your live GPS location.
              </p>

              <button
                onMouseDown={startHold}
                onMouseUp={cancelHold}
                onMouseLeave={cancelHold}
                onTouchStart={startHold}
                onTouchEnd={cancelHold}
                disabled={sending}
                className={`w-full h-32 rounded-2xl text-white font-bold text-lg shadow-lg transition-all ${
                  confirmHold ? 'bg-red-700 scale-95 animate-pulse' : 'bg-red-600 hover:bg-red-700'
                }`}
                data-testid="sos-fire-btn"
              >
                {sending ? 'Sending...' : confirmHold ? 'Hold...' : 'Press & hold to send SOS'}
              </button>

              <button
                onClick={() => setShowContactForm(true)}
                className="w-full text-xs text-gray-500 hover:text-pink-600"
              >
                Update emergency contact
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Set an emergency contact first. They'll be notified the moment you trigger an SOS.
              </p>
              <Button onClick={() => setShowContactForm(true)} className="w-full bg-red-600 hover:bg-red-700">
                <Phone className="h-4 w-4 mr-2" />
                Set emergency contact
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
        <DialogContent className="sm:max-w-sm" data-testid="emergency-contact-form">
          <DialogHeader><DialogTitle>Emergency Contact</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Mom" data-testid="emergency-contact-name" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+260 9XX XXX XXX" data-testid="emergency-contact-phone" />
            </div>
            <div>
              <Label>Relationship (optional)</Label>
              <Input value={form.relationship} onChange={e => setForm({ ...form, relationship: e.target.value })} placeholder="Mother / Spouse / Friend" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowContactForm(false)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={saveContact} data-testid="emergency-contact-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SOSButton;
